import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  Extension,
  ExtensionFilters,
  CreateExtensionDTO,
  UpdateExtensionDTO,
  getAllExtensions,
  getExtensionById,
  createExtension,
  updateExtension,
  deleteExtension,
  getExtensionsByCategory,
} from '@/services/Extensions/extensions.service';

export const extensionsApiSlice = createApi({
  reducerPath: 'extensionsApi',
  tagTypes: ['Extensions', 'Extension'],
  baseQuery: fakeBaseQuery(),
  endpoints: (builder) => ({
    // Extension endpoints
    getAllExtensions: builder.query<
      Extension[],
      { organizationId: string; filters?: ExtensionFilters }
    >({
      queryFn: async ({ organizationId, filters }) => {
        try {
          const data = await getAllExtensions(organizationId, filters);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: (result) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: 'Extensions' as const,
                id: _id,
              })),
              { type: 'Extensions', id: 'LIST' },
            ]
          : [{ type: 'Extensions', id: 'LIST' }],
    }),

    getExtensionById: builder.query<Extension, string>({
      queryFn: async (id) => {
        try {
          const data = await getExtensionById(id);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: (result) => (result ? [{ type: 'Extension', id: result._id }] : []),
    }),

    getExtensionsByCategory: builder.query<
      Extension[],
      { organizationId: string; category: string }
    >({
      queryFn: async ({ organizationId, category }) => {
        try {
          const data = await getExtensionsByCategory(organizationId, category);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      providesTags: (result, category) =>
        result
          ? [
              ...result.map(({ _id }) => ({
                type: 'Extensions' as const,
                id: _id,
              })),
              { type: 'Extensions', id: 'CATEGORY_' + category },
            ]
          : [{ type: 'Extensions', id: 'CATEGORY_' + category }],
    }),

    createExtension: builder.mutation<Extension, CreateExtensionDTO>({
      queryFn: async (extensionData) => {
        try {
          const data = await createExtension(extensionData);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: [{ type: 'Extensions', id: 'LIST' }],
    }),

    updateExtension: builder.mutation<Extension, { id: string; extensionData: UpdateExtensionDTO }>(
      {
        queryFn: async ({ id, extensionData }) => {
          try {
            const data = await updateExtension(id, extensionData);
            return { data };
          } catch (error) {
            return { error: { status: 'FETCH_ERROR', error: String(error) } };
          }
        },
        invalidatesTags: (result) =>
          result
            ? [
                { type: 'Extensions', id: 'LIST' },
                { type: 'Extension', id: result._id },
              ]
            : [{ type: 'Extensions', id: 'LIST' }],
      },
    ),

    deleteExtension: builder.mutation<{ success: boolean; message: string }, string>({
      queryFn: async (id) => {
        try {
          const data = await deleteExtension(id);
          return { data };
        } catch (error) {
          return { error: { status: 'FETCH_ERROR', error: String(error) } };
        }
      },
      invalidatesTags: [{ type: 'Extensions', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetAllExtensionsQuery,
  useGetExtensionByIdQuery,
  useGetExtensionsByCategoryQuery,
  useCreateExtensionMutation,
  useUpdateExtensionMutation,
  useDeleteExtensionMutation,
} = extensionsApiSlice;
